import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';

/**
 * Schedule RULES API of the new «الجدول» page (backend ScheduleRulesController, SCHEDULE-BUILD-PLAN.md P1 item 7).
 *
 *   GET    /campaign-schedules/overview               -> { rules[], standing[], screens_total }
 *   POST   /campaign-schedules/rules                  create (a batch of rows); 409 = unresolved overlap, 402 = Pro feature
 *   PUT    /campaign-schedules/rules/{key}            edit in place (key = batch id, or a legacy row's id)
 *   DELETE /campaign-schedules/rules/{key}
 *   POST   /campaign-schedules/rules/{key}/toggle     { is_active }
 *   POST   /campaign-schedules/conflicts              read-only save-time preview -> { has_conflict, conflicts[], suggested }
 *   POST   /campaign-schedules/status-many            Pro: now / next per screen -> { screens[], tally[], truncated }
 *
 * The backend wraps in a Laravel resource ({ data: { status, ... } }); every call unwraps with `res?.data ?? res`.
 * Screens come from the existing company endpoints: `/groups/company` (groups with their screens) and `/company/devices/all`
 * (every screen, also an ungrouped one, with its resolved time zone).
 */
export const OVERVIEW_KEY = 'scheduleRulesOverview';
const unwrap = (res) => res?.data ?? res;

export const useScheduleOverview = (enabled = true) =>
  useQuery(OVERVIEW_KEY, async () => unwrap((await axiosInstance.get('/campaign-schedules/overview')).data), {
    enabled,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

/** Groups with their screens + every screen: one list for the picker, the target chips and the preview. */
export const useScheduleScreens = (enabled = true) =>
  useQuery('scheduleScreens', async () => {
    const [g, d] = await Promise.all([
      axiosInstance.get('/groups/company'),
      axiosInstance.get('/company/devices/all'),
    ]);
    return buildScreens(unwrap(g.data), unwrap(d.data));
  }, { enabled, staleTime: 30_000 });

/**
 * [{ id, name, screens: [{ id, name, tz, version }] }] in the company's own group order; screens that are in no group go in a
 * last group with `id: null` (they are still covered by an "all screens" rule).
 */
export function buildScreens(groupsRes, devicesRes) {
  const groups = Array.isArray(groupsRes) ? groupsRes : [];
  const devices = Array.isArray(devicesRes) ? devicesRes : [];
  const info = new Map(devices.map((d) => [d.id, d]));
  const seen = new Set();
  const out = groups.map((g) => ({
    id: g.id,
    name: g.name,
    screens: (g.devices || []).filter((s) => s.status !== 'EXPIRED').map((s) => {
      seen.add(s.id);
      const i = info.get(s.id);
      return { id: s.id, name: s.name || s.serial_name, tz: i?.timezone || s.resolved_timezone || null, version: Number(s.device_info?.version_code) || null, paused: s.schedule_paused || null };
    }),
  })).filter((g) => g.screens.length > 0);
  const loose = devices.filter((d) => !seen.has(d.id)).map((d) => ({ id: d.id, name: d.name || d.serial_name, tz: d.timezone || null, version: null, paused: null }));
  if (loose.length) out.push({ id: null, name: null, screens: loose });
  return out;
}

/** now / next of the screens of one target (Pro only: the route carries the scheduling_pro gate). */
export const useStatusMany = (body, enabled = true) =>
  useQuery(['scheduleStatusMany', body], async () => unwrap((await axiosInstance.post('/campaign-schedules/status-many', body)).data), {
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
  });

/** The save-time overlap check (called when the review step opens). `ruleId` = the rule being edited. */
export async function fetchConflicts(payload, ruleId) {
  const body = ruleId ? { ...payload, rule_id: ruleId } : payload;
  return unwrap((await axiosInstance.post('/campaign-schedules/conflicts', body)).data);
}

export const useSaveRule = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ key, payload }) => (key
      ? axiosInstance.put(`/campaign-schedules/rules/${key}`, payload)
      : axiosInstance.post('/campaign-schedules/rules', payload)),
    { onSuccess: () => qc.invalidateQueries([OVERVIEW_KEY]).then(() => qc.invalidateQueries('scheduleStatusMany')) },
  );
};

export const useToggleRule = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ key, is_active }) => axiosInstance.post(`/campaign-schedules/rules/${key}/toggle`, { is_active }),
    { onSuccess: () => qc.invalidateQueries([OVERVIEW_KEY]).then(() => qc.invalidateQueries('scheduleStatusMany')) },
  );
};

export const useDeleteRule = () => {
  const qc = useQueryClient();
  return useMutation(
    (key) => axiosInstance.delete(`/campaign-schedules/rules/${key}`),
    { onSuccess: () => qc.invalidateQueries([OVERVIEW_KEY]).then(() => qc.invalidateQueries('scheduleStatusMany')) },
  );
};

/* ───────────────────────── schedule v4: manual change, end now, resume, occasions ───────────────────────── */

/**
 *   GET  /campaign-schedules/holds/preview?where[mode]=&where[ids][]=   which of these screens are inside a schedule window NOW
 *        -> { count, screens: [{ device_id, name, rule_id, schedule_name, until_local, until_at (UTC ISO), already_paused }] }
 *   POST /campaign-schedules/rules/{key}/end-now                        end a rule's playing windows on the screens inside one -> { ended, screens }
 *   POST /campaign-schedules/holds/resume  { device_ids[], rule_key? }  -> { resumed }
 *   POST /campaign/assign/devices          (the existing manual assign) -> adds { schedule_paused, schedule_paused_screens } when a window was held
 *   GET  /campaign-schedules/occasions[?country=XX]                     -> { country, country_source, items: [{ id, name_ar, type nat|isl|gen, start, days, approximate }] }
 */
export const fetchHoldsPreview = async (where) => {
  // Laravel reads where[mode] / where[ids][]; axios 0.x would send a nested object as JSON
  const params = new URLSearchParams();
  params.append('where[mode]', where.mode);
  (where.ids || []).forEach((id) => params.append('where[ids][]', id));
  return unwrap((await axiosInstance.get('/campaign-schedules/holds/preview', { params })).data);
};

export const useHoldsPreview = (where, enabled = true) =>
  useQuery(['scheduleHoldsPreview', where], () => fetchHoldsPreview(where), { enabled, staleTime: 0, cacheTime: 0, retry: false });

const invalidateSchedule = (qc) => Promise.all([
  qc.invalidateQueries([OVERVIEW_KEY]), qc.invalidateQueries('scheduleStatusMany'), qc.invalidateQueries('scheduleScreens'),
  qc.invalidateQueries('scheduleHoldsPreview'), qc.invalidateQueries('getGroupedDevices'), qc.invalidateQueries('getDevice'),
]);

export const useEndNow = () => {
  const qc = useQueryClient();
  return useMutation(async (keys) => {
    const out = [];
    for (const key of keys) out.push(unwrap((await axiosInstance.post(`/campaign-schedules/rules/${key}/end-now`, { via: 'web' })).data));
    return out;
  }, { onSuccess: () => invalidateSchedule(qc) });
};

export const useResumeHold = () => {
  const qc = useQueryClient();
  return useMutation(
    (deviceIds) => axiosInstance.post('/campaign-schedules/holds/resume', { device_ids: deviceIds }).then((r) => unwrap(r.data)),
    { onSuccess: () => invalidateSchedule(qc) },
  );
};

/** The manual change the page's own dialog sends (the dialog IS the confirmation, so this path is not guarded again). */
export const useManualChange = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ deviceIds, campaignId }) => axiosInstance.post('/campaign/assign/devices', { type: 'devices', devices_ids: deviceIds, campaign_id: campaignId }).then((r) => unwrap(r.data)),
    { onSuccess: () => invalidateSchedule(qc) },
  );
};

export const useOccasions = (country, enabled = true) =>
  useQuery(['scheduleOccasions', country || ''], async () => unwrap((await axiosInstance.get('/campaign-schedules/occasions', { params: country ? { country } : {} })).data), {
    enabled, staleTime: 5 * 60_000, retry: false,
  });
